/**
 * Enhanced SEO validation and optimization utilities
 */

import { SwissLanguage } from './languageTypes';

interface SEOValidationResult {
  canonical: boolean;
  hreflang: boolean;
  schema: boolean;
  errors: string[];
}

/**
 * Validate current page SEO implementation
 */
export const validatePageSEO = (): SEOValidationResult => {
  const result: SEOValidationResult = {
    canonical: false,
    hreflang: false, 
    schema: false,
    errors: []
  };

  // Check canonical tag
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    const href = canonical.getAttribute('href');
    const currentUrl = window.location.href;
    
    if (href === currentUrl) {
      result.canonical = true;
    } else {
      result.errors.push(`Canonical mismatch: ${href} vs ${currentUrl}`);
    }
  } else {
    result.errors.push('Missing canonical tag');
  }

  // Check hreflang tags
  const hreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
  if (hreflangs.length >= 4) {
    result.hreflang = true;
  } else {
    result.errors.push(`Insufficient hreflang tags: ${hreflangs.length}/4`);
  }

  // Check JSON-LD schema
  const schemas = document.querySelectorAll('script[type="application/ld+json"]');
  if (schemas.length >= 2) {
    result.schema = true;
  } else {
    result.errors.push(`Missing schema markup: ${schemas.length} found`);
  }

  return result;
};

/**
 * Generate proper hreflang for EU vs Swiss pages
 */
export const generateOptimizedHreflang = (
  currentPath: string,
  isSwissPage: boolean = false
): Array<{ hreflang: string; href: string }> => {
  const baseUrl = window.location.origin;
  const pathParts = currentPath.split('/').filter(Boolean);
  
  if (pathParts.length < 2) return [];
  
  const pathWithoutLang = pathParts.slice(1).join('/');
  const alternates = [];
  
  if (isSwissPage) {
    // Swiss pages: use country-specific hreflang
    const swissLangs = [
      { lang: 'de', hreflang: 'de-CH' },
      { lang: 'en', hreflang: 'en-CH' },
      { lang: 'fr', hreflang: 'fr-CH' },
      { lang: 'it', hreflang: 'it-CH' }
    ];
    
    swissLangs.forEach(({ lang, hreflang }) => {
      alternates.push({
        hreflang,
        href: `${baseUrl}/${lang}/${pathWithoutLang}`
      });
    });
    
    // x-default to German for Swiss
    alternates.push({
      hreflang: 'x-default',
      href: `${baseUrl}/de/${pathWithoutLang}`
    });
  } else {
    // EU pages: use generic language codes
    const languages = ['de', 'en', 'fr', 'it'];
    
    languages.forEach(lang => {
      alternates.push({
        hreflang: lang,
        href: `${baseUrl}/${lang}/${pathWithoutLang}`
      });
    });
    
    // x-default to English for EU
    alternates.push({
      hreflang: 'x-default', 
      href: `${baseUrl}/en/${pathWithoutLang}`
    });
  }
  
  return alternates;
};

/**
 * Update page meta tags with enhanced SEO
 */
export const updateEnhancedPageSEO = (config: {
  title: string;
  description: string;
  keywords?: string;
  currentPath: string;
  isSwissPage?: boolean;
  language: SwissLanguage;
  service?: string;
  cityKey?: string;
}) => {
  const { title, description, keywords, currentPath, isSwissPage = false, language, service, cityKey } = config;
  const baseUrl = window.location.origin;
  
  // Update title
  document.title = title;
  
  // Update meta description
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute('content', description);
  
  // Update keywords if provided
  if (keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);
  }
  
  // Remove existing canonical and hreflang
  document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach(el => el.remove());
  
  // Add canonical (self-referencing)
  const canonical = document.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = `${baseUrl}${currentPath}`;
  document.head.appendChild(canonical);
  
  // Add optimized hreflang
  const alternates = generateOptimizedHreflang(currentPath, isSwissPage);
  alternates.forEach(alt => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alt.hreflang;
    link.href = alt.href;
    document.head.appendChild(link);
  });
  
  // Add Open Graph tags
  const updateOGTag = (property: string, content: string) => {
    let ogTag = document.querySelector(`meta[property="${property}"]`);
    if (!ogTag) {
      ogTag = document.createElement('meta');
      ogTag.setAttribute('property', property);
      document.head.appendChild(ogTag);
    }
    ogTag.setAttribute('content', content);
  };
  
  updateOGTag('og:title', title);
  updateOGTag('og:description', description);
  updateOGTag('og:url', `${baseUrl}${currentPath}`);
  updateOGTag('og:type', 'website');
  updateOGTag('og:locale', isSwissPage ? `${language}_CH` : language);
  
  // Add Twitter Card
  const updateTwitterTag = (name: string, content: string) => {
    let twitterTag = document.querySelector(`meta[name="${name}"]`);
    if (!twitterTag) {
      twitterTag = document.createElement('meta');
      twitterTag.setAttribute('name', name);
      document.head.appendChild(twitterTag);
    }
    twitterTag.setAttribute('content', content);
  };
  
  updateTwitterTag('twitter:card', 'summary_large_image');
  updateTwitterTag('twitter:title', title);
  updateTwitterTag('twitter:description', description);
};

/**
 * Generate Rich Results compliant LocalBusiness schema
 */
export const generateLocalBusinessSchema = (cityKey: string, service: string, language: SwissLanguage) => {
  const baseUrl = window.location.origin;
  
  // City data mapping
  const cityData: Record<string, any> = {
    'zurich': { name: 'Zurich', country: 'Switzerland', lat: 47.3769, lng: 8.5417 },
    'geneva': { name: 'Geneva', country: 'Switzerland', lat: 46.2044, lng: 6.1432 },
    'basel': { name: 'Basel', country: 'Switzerland', lat: 47.5596, lng: 7.5886 },
    'stuttgart': { name: 'Stuttgart', country: 'Germany', lat: 48.7758, lng: 9.1829 },
    'munich': { name: 'Munich', country: 'Germany', lat: 48.1351, lng: 11.5820 },
    'lyon': { name: 'Lyon', country: 'France', lat: 45.7640, lng: 4.8357 },
    'paris': { name: 'Paris', country: 'France', lat: 48.8566, lng: 2.3522 },
    'milan': { name: 'Milan', country: 'Italy', lat: 45.4642, lng: 9.1900 },
    'como': { name: 'Como', country: 'Italy', lat: 45.8081, lng: 9.0852 }
  };
  
  const city = cityData[cityKey];
  if (!city) return null;
  
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Future Level - ${service} ${city.name}`,
    "description": `Professional ${service} services in ${city.name}, ${city.country}`,
    "url": baseUrl,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city.name,
      "addressCountry": city.country === 'Switzerland' ? 'CH' : city.country === 'Germany' ? 'DE' : city.country === 'France' ? 'FR' : 'IT'
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": city.lat,
      "longitude": city.lng
    },
    "areaServed": {
      "@type": "City",
      "name": city.name
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5"
    },
    "priceRange": "CHF 2000 - CHF 50000"
  };
};